import { TimesheetDay } from "domain/entities/timesheet-day";
import { PurchaseOrder } from "domain/entities/purchase-order";
import { Parameter } from "domain/entities/parameter";
import { EMAIL } from "domain/enum/email";
import { abscenceTypesInDays } from "presentation/mobile/utils/abscence-types-in-days.util";

export interface BuildMultiDayPayloadParams {
    selectedDates: Date[];
    currentDays: TimesheetDay[];
    entryType: "project" | "abscence";
    selectedPO: string | null;
    selectedAbscence: string | null;
    purchaseOrders?: PurchaseOrder[];
    abscenceOptions: Parameter[];
    hours: number;
    isDeslocacaoOrPrevencao: boolean;
    overwrite: boolean;
    timesheetId?: string;
    userEmployeeEmail?: string | null;
}

export const toId = (value: any): string | undefined => {
    if (!value) return undefined;
    if (typeof value === "string") return value;
    if (typeof value === "object" && "id" in value) {
        const inner = (value as any).id;
        return typeof inner === "string" ? inner : toId(inner);
    }
    return undefined;
};

export const buildMultiDayPayload = ({
    selectedDates,
    currentDays,
    entryType,
    selectedPO,
    selectedAbscence,
    purchaseOrders,
    abscenceOptions,
    hours,
    isDeslocacaoOrPrevencao,
    overwrite,
    timesheetId,
    userEmployeeEmail,
}: BuildMultiDayPayloadParams): TimesheetDay[] => {
    const poId = toId(selectedPO);
    const abscenceId = toId(selectedAbscence);
    const actualHours = isDeslocacaoOrPrevencao ? 0 : hours;

    return selectedDates.map((date) => {
        const day = date.getDate();
        const existing = currentDays.find((d) => d.day === day);

        const existingPOs = (existing?.purchaseOrders ?? [])
            .map((po) => ({
                id: toId(po.id),
                name: po.name ?? "",
                hours: po.hours ?? 0,
            }))
            .filter((po) => po.id) as { id: string; name: string; hours: number }[];

        const existingAbscence = existing?.abscenceType
            ? {
                id: toId(existing.abscenceType.id),
                name: existing.abscenceType.name ?? "",
                hours: existing.abscenceTime ?? existing.abscenceType.hours ?? 0,
            }
            : null;

        const validExistingAbscence =
            !overwrite &&
                existingAbscence &&
                existingAbscence.id &&
                existingAbscence.hours > 0
                ? {
                    id: existingAbscence.id,
                    name: existingAbscence.name,
                    hours: existingAbscence.hours,
                }
                : undefined;

        // Alocação em Projeto 
        if (entryType === "project" && poId) {
            const poObj = purchaseOrders?.find((po) => po.id === poId);
            const poName = poObj?.name ?? "";

            let updatedPOs = [];
            if (overwrite) {
                updatedPOs = [{ id: poId, name: poName, hours: actualHours }];
            } else {
                const existingWithoutCurrent = existingPOs.filter((po) => po.id !== poId);
                updatedPOs = [...existingWithoutCurrent, { id: poId, name: poName, hours: actualHours }];
            }

            return {
                id: existing?.id,
                day,
                timesheet: { id: timesheetId },
                lastModifiedBy: userEmployeeEmail ?? EMAIL,
                purchaseOrders: updatedPOs,
                abscenceType: validExistingAbscence,
                abscenceTime: validExistingAbscence ? validExistingAbscence.hours : 0,
            };
        }

        // Justificação de Ausência 
        if (entryType === "abscence" && abscenceId) {
            const abName = abscenceOptions.find((o) => o.id === abscenceId)?.value ?? "";

            return {
                id: existing?.id,
                day,
                timesheet: { id: timesheetId },
                lastModifiedBy: userEmployeeEmail ?? EMAIL,
                purchaseOrders: overwrite ? [] : existingPOs,
                abscenceType: {
                    id: abscenceId,
                    name: abName,
                    hours,
                },
                abscenceTime: hours,
            };
        }

        // Fallback
        return {
            id: existing?.id,
            day,
            timesheet: { id: timesheetId },
            lastModifiedBy: userEmployeeEmail ?? EMAIL,
            purchaseOrders: existingPOs,
            abscenceType: validExistingAbscence,
            abscenceTime: validExistingAbscence ? validExistingAbscence.hours : 0,
        };
    });
};

export const MAX_HOURS_PER_DAY = 14;

export interface MultiDayConflict {
    day: number;
    reason: string;
}

export const findMultiDayConflicts = ({
    payload,
    currentDays,
    entryType,
    overwrite,
    extraHourPoIds = new Set<string>(),
}: {
    payload: TimesheetDay[];
    currentDays: TimesheetDay[];
    entryType: "project" | "abscence";
    overwrite: boolean;
    /** IDs das POs de Hora Extra (excluídas do limite diário de 14h) */
    extraHourPoIds?: Set<string>;
}): MultiDayConflict[] => {
    const conflicts: MultiDayConflict[] = [];

    payload.forEach((item) => {
        const existing = currentDays.find((d) => d.day === item.day);
        const existingAbscenceId = toId(existing?.abscenceType?.id);
        const existingHasAbscence = !!existingAbscenceId && (existing?.abscenceTime ?? 0) > 0;
        const existingIsDailyAbscence =
            existingHasAbscence && abscenceTypesInDays.includes(existing?.abscenceType?.name ?? "");
        const isSameAbscence =
            entryType === "abscence" && existingAbscenceId === toId(item.abscenceType?.id);

        if (!overwrite) {
            if (existingIsDailyAbscence && !isSameAbscence) {
                conflicts.push({ day: item.day, reason: "Já existe uma ausência diária registada" });
                return;
            }

            const isNewDailyAbscence =
                entryType === "abscence" && abscenceTypesInDays.includes(item.abscenceType?.name ?? "");
            if (isNewDailyAbscence && (existing?.purchaseOrders?.length ?? 0) > 0) {
                conflicts.push({ day: item.day, reason: "Já existe um registo de projeto" });
                return;
            }

            if (entryType === "abscence" && existingHasAbscence && !isSameAbscence) {
                conflicts.push({ day: item.day, reason: "Já existe uma ausência registada" });
                return;
            }
        }

        // Horas extra não contam para o limite diário de 14h
        const totalHours =
            (item.purchaseOrders ?? [])
                .filter((po) => {
                    const poId = toId(po.id);
                    return !(poId && extraHourPoIds.has(poId));
                })
                .reduce((sum, po) => sum + (po.hours ?? 0), 0) +
            (item.abscenceTime ?? 0);

        if (totalHours > MAX_HOURS_PER_DAY) {
            conflicts.push({ day: item.day, reason: `Excede o limite de ${MAX_HOURS_PER_DAY} horas` });
        }
    });

    return conflicts;
};
